import React from 'react';
import { Heart, MessageCircle, Share2, ThumbsUp, Award } from 'lucide-react';
import { Post } from '../../types';

interface PostCardProps {
  post: Post;
  onPostInteraction: (postId: string, action: string) => void;
  onSharePost: (post: Post) => void;
  onViewProfile: (userId: string) => void;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  onPostInteraction,
  onSharePost,
  onViewProfile
}) => {
  // Count reactions by type
  const helpfulCount = post.reactions.filter(r => r.type === 'helpful').length;
  const heartCount = post.reactions.filter(r => r.type === 'heart').length;
  const thanksCount = post.reactions.filter(r => r.type === 'thanks').length;
  
  // Check if post content has a title (starts with **Title**)
  const hasTitle = post.content.startsWith('**') && post.content.includes('**\n\n');
  let title = '';
  let content = post.content;
  
  if (hasTitle) {
    const titleMatch = post.content.match(/^\*\*(.*?)\*\*\n\n([\s\S]*)/);
    if (titleMatch) {
      title = titleMatch[1];
      content = titleMatch[2];
    }
  }
  
  return (
    <div className="card-base card-padding-md dark:bg-gray-800 dark:border-gray-700">
      {/* Author Info */}
      <div className="flex items-start space-x-3 mb-4">
        <button
          onClick={() => !post.isAnonymous && onViewProfile(post.authorId)}
          className="avatar-md hover:scale-110 transition-transform duration-200"
        >
          {post.isAnonymous ? '?' : post.authorName.charAt(0)}
        </button>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <button
              onClick={() => !post.isAnonymous && onViewProfile(post.authorId)}
              className="font-medium text-gray-900 dark:text-gray-100 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200"
            >
              {post.isAnonymous ? 'Anonymous' : post.authorName}
            </button>
            {post.isExpert && !post.isAnonymous && (
              <span className="badge-success animate-pulse flex items-center space-x-1">
                <Award size={12} />
                <span>Expert</span>
              </span>
            )}
            <span className="text-gray-500 dark:text-gray-400 text-sm">{post.timestamp}</span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm capitalize">{post.category.replace('-', ' ')}</p>
        </div>
      </div>
      
      {/* Post Content */}
      <div className="mb-6">
        {title && (
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">{title}</h3>
        )}
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-line">{content}</p>
        </div>
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {post.tags.map((tag, index) => (
              <span key={index} className="badge-secondary">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Interaction Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => onPostInteraction(post.id, 'helpful')}
            className="flex items-center space-x-2 px-3 py-2 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full text-sm hover:bg-green-200 dark:hover:bg-green-800 transition-all duration-200 hover:scale-105"
          >
            <ThumbsUp size={16} />
            <span>So Strong</span>
            <span className="font-medium">{helpfulCount}</span>
          </button>
          <button 
            onClick={() => onPostInteraction(post.id, 'heart')}
            className="flex items-center space-x-2 px-3 py-2 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-full text-sm hover:bg-red-200 dark:hover:bg-red-800 transition-all duration-200 hover:scale-105"
          >
            <Heart size={16} />
            <span>Heart</span>
            <span className="font-medium">{heartCount}</span>
          </button>
          <button 
            onClick={() => onPostInteraction(post.id, 'thanks')}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full text-sm hover:bg-blue-200 dark:hover:bg-blue-800 transition-all duration-200 hover:scale-105"
          >
            <span>🙏</span>
            <span>Thanks</span>
            <span className="font-medium">{thanksCount}</span>
          </button>
        </div>
        
        <button 
          onClick={() => onSharePost(post)}
          className="flex items-center space-x-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
        >
          <Share2 size={16} />
          <span className="text-sm">Share</span>
        </button>
      </div>
    </div>
  );
};

export default PostCard;